import React, { useRef } from 'react';
import { haptic } from '../../utils/haptics';

/**
 * Kanonische Tab-Leiste des Design-Systems (Phase 4 — Layout & Dichte).
 *
 * Ersetzt 10+ handgebaute Leisten, von denen mehrere auf 375px abgeschnitten haben.
 *
 * Kernregel: **Nichts wird je abgeschnitten.** Statt horizontal zu scrollen (was
 * Tabs unsichtbar macht) schrumpft bzw. umbricht die Leiste:
 *  - variant="underline" — schrumpft: kleinere Paddings/Schrift auf Mobile, Icons
 *    erst ab sm:, optional `shortLabel` fürs Telefon.
 *  - variant="pill"      — bricht um (flex-wrap) statt zu scrollen.
 *
 * Farben kommen ausschließlich aus Tokens. Damit entfallen die bisherigen
 * dynamisch zusammengesetzten Klassennamen (`bg-${tab.color}-500`), die nur
 * zufällig funktionierten, solange dieselbe Farbe anderswo als Literal vorkam.
 *
 * items: [{ key, label, shortLabel?, icon?, count? }]
 *
 * `icon` gibt es technisch weiter, aber ein Reiter ist Text ODER Icon, nie
 * gemischt (Oktober 2026): Symbol und Wort nebeneinander sagen dasselbe zweimal.
 *
 * Haptik (Oktober 2026): Ein Wechsel löst `haptic.selection()` aus, und zwar
 * hier im Baustein — Aufrufer ergänzen keine eigene Haptik. Ein Tipp auf den
 * schon aktiven Reiter bleibt stumm.
 */

// Optional verkettet, weil Tests `utils/haptics` oft nur teilweise mocken.
const tick = () => haptic?.selection?.();

const isActiveKey = (itemKey, value) => itemKey === value;

const Tabs = ({
  items = [],
  value,
  onChange,
  variant = 'underline',
  fill = false,
  className = '',
  ariaLabel = 'Bereiche',
}) => {
  const tabRefs = useRef([]);

  // Pfeiltasten-Navigation gemäß WAI-ARIA Tabs-Pattern.
  const handleKeyDown = (event, index) => {
    const lastIndex = items.length - 1;
    let nextIndex = null;

    if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = lastIndex;

    if (nextIndex === null) return;
    event.preventDefault();
    if (items[nextIndex].key !== value) tick();
    onChange(items[nextIndex].key);
    tabRefs.current[nextIndex]?.focus();
  };

  const isPill = variant === 'pill';

  // `fill` verteilt die Tabs gleichmaessig ueber die volle Breite — das Muster fuer
  // Leisten direkt unter einem Seitenkopf. Ohne diese Option draengen sich zwei,
  // drei Tabs am linken Rand und der Rest der Zeile bleibt leer. Umbruch ist dann
  // aus, sonst wandert bei Platzmangel ein einzelner Tab in eine zweite Zeile.
  const listClasses = isPill
    ? `flex ${fill ? '' : 'flex-wrap'} gap-2 ${className}`
    : `flex ${fill ? '' : 'flex-wrap'} border-b border-border ${className}`;

  return (
    <div role="tablist" aria-label={ariaLabel} className={listClasses}>
      {items.map((item, index) => {
        const active = isActiveKey(item.key, value);
        const Icon = item.icon;

        // Unterstrich-Variante (Oktober 2026): `items-stretch`, damit der
        // Inhalt die volle Knopfhöhe einnimmt — der Strich unter ihm sitzt
        // dann genau auf der Grundlinie der Leiste.
        const base =
          `inline-flex ${isPill ? 'items-center' : 'items-stretch'} justify-center gap-1.5 min-h-[44px] font-medium ` +
          'transition-colors duration-base ease-standard touch-manipulation ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary';

        const fillClass = fill ? 'flex-1 min-w-0' : '';

        const sizing = isPill
          ? `px-3.5 py-2 text-xs sm:px-4 sm:text-sm rounded-full ${fillClass}`
          : `px-2 text-xs sm:px-4 sm:text-sm ${fillClass}`;

        // Aktiv = gefuellte Pille in Tinte (Okt 2026, „App wie Spot“). Im
        // August war die Fuellung lila und deshalb ein zweiter lauter Farbblock
        // neben den Primaerknoepfen — die Loesung damals war eine helle Flaeche
        // mit lila Schrift. Der Werbespot zeigt die dritte Moeglichkeit: Tinte.
        // Sie ist so eindeutig wie die alte Fuellung und verbraucht kein Lila
        // (CorporateDesign: hoechstens drei lila Elemente pro Bildschirm).
        //
        // Unterstrich (Oktober 2026): Der Strich ist kein Rahmen des Knopfes
        // mehr, sondern ein eigener Strich unter dem Inhalt (siehe unten). Als
        // `border-b-2` lief er über die volle Knopfbreite samt Polster, in
        // einer `fill`-Leiste also bis an den Nachbarn heran; jetzt ist er so
        // breit wie das Label.
        const tone = isPill
          ? active
            ? 'bg-foreground text-background border border-foreground font-semibold'
            : 'bg-surface-raised text-muted-foreground border border-border hover:text-foreground'
          : active
            ? 'text-foreground'
            : 'text-muted-foreground hover:text-foreground';

        const content = (
          <>
            {Icon && <Icon className="hidden sm:inline w-4 h-4 shrink-0" aria-hidden="true" />}
            {/* shortLabel spart auf dem Telefon Platz, ohne den Tab zu verstecken */}
            {item.shortLabel ? (
              <>
                <span className="sm:hidden">{item.shortLabel}</span>
                <span className="hidden sm:inline">{item.label}</span>
              </>
            ) : (
              <span>{item.label}</span>
            )}
            {/* Der Zaehler bleibt neutral, auch im aktiven Tab: Welcher Tab
                gewaehlt ist, sagt schon die erhabene Flaeche unter ihm. Ein
                zusaetzlich eingefaerbtes Zahlenpuenktchen wiederholt diese
                Information nur — und zwar lauter als die Zahl es verdient. */}
            {typeof item.count === 'number' && item.count > 0 && (
              <span
                className={`ml-1 shrink-0 rounded-full px-1.5 py-0.5 text-2xs font-semibold ${
                  active && isPill
                    ? 'bg-background/20 text-background'
                    : active
                      ? 'bg-foreground/10 text-foreground'
                      : 'bg-foreground/10 text-muted-foreground'
                }`}
              >
                {item.count}
              </span>
            )}
          </>
        );

        return (
          <button
            key={item.key}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${item.key}`}
            aria-selected={active}
            aria-controls={`tabpanel-${item.key}`}
            tabIndex={active ? 0 : -1}
            onClick={() => {
              if (item.key !== value) tick();
              onChange(item.key);
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={`${base} ${sizing} ${tone}`}
          >
            {isPill ? content : (
              <span className="relative inline-flex min-w-0 items-center gap-1.5 py-3">
                {content}
                {active && (
                  <span aria-hidden="true" className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-foreground" />
                )}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
